const isNode = typeof window === 'undefined';

if (isNode) {
  // @ts-expect-error
  import.meta.env = process.env;
}

interface Config {
  appEnv: string;
  baseDomain: string;
  harnessApiUrl: string;
  apiUrl: string;
  apiProxy: boolean;
  rewriteUrl?: string;
  webUrl: string;
  cdnUrl?: string;
  mainSiteUrl: string;
  webSocketUrl: string;
  blogApiUrl: string;
  helpCenterUrl: string;
  filestackEngagementAttachmentMimeTypes: string[];
  enableInviteParticipant: boolean;
  disableWrittenEngagement: boolean;
  disableChangeExpertRequestType: boolean;
  recaptchaSiteKey?: string;
  googleTagManagerId?: string;
  growthBookClientKey?: string;
  cognitoUserPoolId?: string;
  cognitoClientId?: string;
  cognitoRegion?: string;
  googleClientId?: string;
  microsoftClientId?: string;
  enableChat: boolean;
  enableConsultingProjectAccountIds: string[];
  enableOpportunityCallAccountIds: string[];
  stripePublishableKey: string;
  enableExpertPaymentsConsole: boolean;
  enableBillingPortal: boolean;
  enablePayoutDashboard: boolean;
  disableAgreementGate: boolean;
  filestackApiKey: string;
  intercomAppId: string;
  sendbirdAppId: string;
  sentryDsn: string;
  smbUtmSources: string[];
  calendlyUrl: string;

  // Harness
  clientName: string;
  clientAssetsName: string;
  productName: string;
  logo: string;
  icon: string;
  accessIcon: string;
  onboardingVideo: number;
}

const webUrl = new URL(import.meta.env.VITE_WEB_URL || `http://localhost:5173`);
const baseDomain = webUrl.hostname;

const apiUrl = new URL(import.meta.env.VITE_API_URL || `http://${baseDomain}:15000`);

const config: Config = {
  appEnv: import.meta.env.VITE_ENV || 'development',
  baseDomain,
  harnessApiUrl: import.meta.env.VITE_HARNESS_PYTHON_API_URL,
  apiUrl: apiUrl.toString().replace(/\/$/, ''),
  apiProxy: import.meta.env.VITE_API_PROXY === '1',
  rewriteUrl: import.meta.env.VITE_REWRITE_URL,
  webUrl: webUrl.toString().replace(/\/$/, ''),
  cdnUrl: import.meta.env.VITE_CDN_URL,
  mainSiteUrl: `https://onfrontiers.com`,
  webSocketUrl:
    import.meta.env.VITE_WEBSOCKET_URL ||
    `${apiUrl.protocol === 'http:' ? 'ws' : 'wss'}://${apiUrl.host}/websocket`,
  blogApiUrl: import.meta.env.VITE_BLOG_API_URL || 'https://onfrontiers.com',
  helpCenterUrl: 'https://support.onfrontiers.com',
  filestackEngagementAttachmentMimeTypes: import.meta.env
    .VITE_FILESTACK_ENGAGEMENT_ATTACHMENT_MIME_TYPES
    ? import.meta.env.VITE_FILESTACK_ENGAGEMENT_ATTACHMENT_MIME_TYPES.split(',')
    : ['text/*', 'image/*', 'application/*'],
  enableChat: !!import.meta.env.VITE_SENDBIRD_APP_ID,
  // TODO: Remove temporary feature flag introduced in EKN-138
  // when feature is ready.
  enableInviteParticipant: import.meta.env.VITE_ENABLE_INVITE_PARTICIPANT === '1',
  disableWrittenEngagement: import.meta.env.VITE_DISABLE_WRITTEN_ENGAGEMENT === '1',
  disableChangeExpertRequestType: import.meta.env.VITE_DISABLE_CHANGE_EXPERT_REQUEST_TYPE === '1',
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  // See ACCESS-27, we are disabling these expert request types for all
  // accounts except for the ones listed below.
  googleTagManagerId: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
  growthBookClientKey: import.meta.env.VITE_GROWTH_BOOK_CLIENT_KEY,
  // ENG-6102: the dev pool's public `web-app` client. Used only when the
  // `cognito-auth` GrowthBook flag is on; absent means the HS256 path only.
  // Not secrets: the client has no secret and both ids ship in the bundle.
  cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || undefined,
  cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || undefined,
  cognitoRegion: import.meta.env.VITE_COGNITO_REGION || undefined,
  // ENG-6165: the Google OAuth web client for "Continue with Google". Used only
  // with Cognito login and the `social-google` flag on. SSM holds a single
  // space where it is not configured, so blank means absent. Not a secret.
  googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() || undefined,
  // ENG-6169: the Entra app for "Continue with Microsoft", with the
  // `social-microsoft` flag and Cognito login. Blank means absent, as above.
  microsoftClientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID?.trim() || undefined,
  // Both *_ACCOUNT_IDS lists below split on ',' with no trim. SSM rejects an
  // empty String parameter value, so a list meant to be empty arrives as " " and
  // resolves to [' '] — a non-empty list holding one blank id.
  //
  // Nothing is broken today, for two different reasons:
  // enableConsultingProjectAccountIds has no consumer at all (dead config,
  // predating ENG-5715), and enableOpportunityCallAccountIds is read only through
  // .includes (RequestConsultation/Dialog.tsx), which [' '] cannot satisfy. A
  // future .length check on either would be the trap. ENG-5715 removed the one
  // helper that handled the quirk, and this note is what it left behind.
  enableConsultingProjectAccountIds: import.meta.env.VITE_ENABLE_CONSULTING_PROJECT_ACCOUNT_IDS
    ? import.meta.env.VITE_ENABLE_CONSULTING_PROJECT_ACCOUNT_IDS.split(',')
    : [],
  // See ACCESS-29, we are disabling these consultation types for all
  // accounts except for the ones listed below.
  enableOpportunityCallAccountIds: import.meta.env.VITE_ENABLE_OPPORTUNITY_CALL_ACCOUNT_IDS
    ? import.meta.env.VITE_ENABLE_OPPORTUNITY_CALL_ACCOUNT_IDS.split(',')
    : [],
  // stripe demo test publishable key
  stripePublishableKey:
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_6pRNASCoBOKtIshFeQd4XMUh',
  // ENG-5624: the internal accounts-payable console at /admin/expert-payments.
  // `=== '1'` with no `|| '1'` fallback, unlike the two flags below it: absent
  // means OFF. This surface moves real money to experts and must not switch
  // itself on in an environment that merely forgot to set the variable. It moves
  // per environment in lockstep with the api's ENABLE_EXPERT_PAYMENTS, which is
  // deliberately 0 everywhere today (ENG-5639) — a console reachable without its
  // api half would render nothing but errors.
  enableExpertPaymentsConsole: import.meta.env.VITE_ENABLE_EXPERT_PAYMENTS_CONSOLE === '1',
  enableBillingPortal: (import.meta.env.VITE_ENABLE_BILLING_PORTAL || '1') === '1',
  enablePayoutDashboard: (import.meta.env.VITE_ENABLE_PAYOUT_DASHBOARD || '1') === '1',
  // ENG-5570. Inverted from the old VITE_REQUIRE_AGREEMENTS, which defaulted
  // the gate OFF when unset. That is how a compliance gate stayed silently
  // inert from 2018 until now, so absent means the gate runs.
  //
  // Set to "1" only to disable the gate during an incident, via the Amplify
  // console followed by a rebuild. Deliberately not wired through terraform: an
  // SSM-backed variable is still read at plan time and baked into the Amplify
  // app config, so writing the parameter changes nothing until someone applies,
  // and the prod plan is currently dirty and cannot be applied. That made the
  // terraform route strictly worse than no route at all.
  //
  // Being VITE_-prefixed this is compiled in, so pulling it costs a rebuild
  // either way. Reverting REQUIRED_EPA_VERSION costs exactly the same and shows
  // up in a diff, so prefer that. This exists only for the case where the
  // constant is not the problem. Note a later `terraform apply` would drop a
  // console-set value, since the env map is authoritative.
  //
  // Remove once the gate is proven in prod.
  disableAgreementGate: import.meta.env.VITE_DISABLE_AGREEMENT_GATE === '1',
  filestackApiKey: import.meta.env.VITE_FILESTACK_API_KEY,
  intercomAppId: import.meta.env.VITE_INTERCOM_APP_ID,
  sendbirdAppId: import.meta.env.VITE_SENDBIRD_APP_ID,
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  smbUtmSources: import.meta.env.VITE_SMB_UTM_SOURCES
    ? import.meta.env.VITE_SMB_UTM_SOURCES.split(',').map((s: string) => s.toLowerCase().trim())
    : [],
  calendlyUrl: import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/bcaouette/15m',

  // Harness
  clientName: (import.meta.env.VITE_CLIENT_NAME || 'Access').replace(/\+/g, ' '),
  clientAssetsName: import.meta.env.VITE_CLIENT_ASSETS_NAME || 'access',
  productName: import.meta.env.VITE_PRODUCT_NAME || 'OnFrontiers',
  logo: `/assets/logo/${import.meta.env.VITE_CLIENT_ASSETS_NAME || 'access'}.png`,
  icon: `/assets/favicon/${import.meta.env.VITE_CLIENT_ASSETS_NAME || 'access'}.ico`,
  accessIcon: `/assets/favicon/access.ico`,
  onboardingVideo: import.meta.env.VITE_ONBOARDING_VIDEO
    ? +import.meta.env.VITE_ONBOARDING_VIDEO
    : 858772190,
};

// KT: fix for node scripts
const location = isNode ? { origin: '' } : window.location;
config.cdnUrl = config.cdnUrl || config.webUrl;
config.apiUrl = config.apiProxy ? `${location.origin}/api` : config.apiUrl;
config.webSocketUrl = config.apiProxy ? `${location.origin}/websocket` : config.webSocketUrl;

export default Object.freeze(config);
